import {
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef
} from 'react';
import axios from 'axios';
import catchCancel from 'helpers/catchCancel';
import { parseServerError } from 'helpers/errorHelpers';
import { Context } from 'components/Store';
import { getAccessToken } from 'utilities/base-auth';
import { CROPWISE_PROXY_URL } from 'utilities/apiConstants';
import { debounce } from 'lodash';

const useTaskProducts = defaultParams => {
  const [{ organization }, dispatch] = useContext(Context);
  const [taskProducts, setTaskProducts] = useState([]);
  const cancelTokenSourceRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [request, setRequest] = useState({
    seasonIdsCommaSeparated: JSON.parse(
      localStorage.getItem('selectedCropSeasons')
    ).join(','),
    ...defaultParams
  });

  const selectedCropSeasonIds = useMemo(() => {
    const cropSeasonIds = request.seasonIdsCommaSeparated.split(',');
    return cropSeasonIds;
  }, [request.seasonIdsCommaSeparated]);

  const setSelectedCropSeasonIds = cropSeasonIds => {
    const seasonIdsCommaSeparated = cropSeasonIds.join(',');
    setRequest(prev => ({
      ...prev,
      seasonIdsCommaSeparated
    }));
  };

  const loadTaskProducts = useCallback(
    params => {
      setLoading(true);

      cancelTokenSourceRef.current = axios.CancelToken.source();

      axios
        .get(`${CROPWISE_PROXY_URL}/v1/base/tasks/products-per-season`, {
          headers: {
            'Content-Type': 'application/json',
            'cwf-context': JSON.stringify({
              organization_id: organization.id
            }),
            Authorization: `Bearer ${getAccessToken()}`
          },
          params: {
            ...request,
            ...params
          },
          cancelToken: cancelTokenSourceRef.current.token
        })
        .then(({ data }) => {
          setTaskProducts(data);
          setError(null);
        })
        .catch(catchCancel)
        .catch(err => {
          setError(err);
          parseServerError(dispatch)(err);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [organization.id, request, dispatch]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedLoadTaskProducts = useCallback(
    debounce(params => {
      loadTaskProducts(params);
    }, 300), // Adjust debounce time as needed (300ms in this case)
    [loadTaskProducts]
  );

  // Cancel the trailing debounce on unmount: it otherwise fires up to 300ms
  // later, fetching and setting state against an unmounted component.
  useEffect(() => () => debouncedLoadTaskProducts.cancel(), [
    debouncedLoadTaskProducts
  ]);

  useEffect(() => {
    if (cancelTokenSourceRef.current) {
      cancelTokenSourceRef.current.cancel(
        'Request canceled due to a new request.'
      );
    }
    debouncedLoadTaskProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request]);

  return {
    taskProducts,
    loading,
    error,
    selectedCropSeasonIds,
    setSelectedCropSeasonIds
  };
};

export default useTaskProducts;
